import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errMsg, tokens, keepaliveJson } from "../api";
import Barbell, { solvePlates, PLATE } from "../components/Barbell";
import AccountMenu from "../components/AccountMenu";
import { InstallBanner } from "../components/InstallPrompt";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

/* ── Web Push (v5) ───────────────────────────────────────────────────────────
 * VAPID public key (NOT secret — the browser needs it to create a push
 * subscription). Must match VAPID_PUBLIC_KEY in the API's .env. Do not change
 * without re-subscribing every device.
 */
const VAPID_PUBLIC_KEY =
  "BDzhBRQfycHPS9jtAUOXioXSilUVqSib8KsPd7y8BO6XOfQ5GAcAkDiQwgarFEaKtIVD80vpBVWh8czVGbZqL2k";

// localStorage key remembering that the student dismissed the in-app permission
// prompt ("Not now"), so we don't nag them every session. Device-local on
// purpose — matches the per-device subscription model.
const PUSH_DISMISSED_KEY = "llh_push_prompt_dismissed";

// Convert the base64url VAPID key to the Uint8Array pushManager.subscribe wants.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const PUSH_SUPPORTED =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/* ── program definition ──────────────────────────────────────────────────── */
const LIFTS = {
  squat: { name: "Squat", sets: 5 },
  bench: { name: "Bench Press", sets: 5 },
  row: { name: "Barbell Row", sets: 5 },
  ohp: { name: "Overhead Press", sets: 5 },
  deadlift: { name: "Deadlift", sets: 1 },
};
const WORKOUT_A = ["squat", "bench", "row"];
const WORKOUT_B = ["squat", "ohp", "deadlift"];
const ORDER = ["squat", "bench", "row", "ohp", "deadlift"];
// Index = server weekday number (0 = Sunday .. 6 = Saturday), matching the
// server's localWeekday(). Sunday-first display.
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DEFAULT_WEIGHTS = { squat: 45, bench: 45, row: 65, ohp: 45, deadlift: 95 };
const DEFAULT_SETTINGS = {
  bar: 45,
  plates: { 55: false, 45: true, 35: false, 25: true, 10: true, 5: true, 2.5: true, 1.25: false },
  increments: { squat: 5, bench: 5, row: 5, ohp: 5, deadlift: 10 },
  next: "A",
  rest: 180,
  barOverrides: {}, // per-lift bar weight overrides; empty = use global bar
};

/* ── helpers ─────────────────────────────────────────────────────────────── */
const r5 = (w) => Math.round(w / 5) * 5;
const e1rm = (w, reps) => (reps > 0 ? Math.round(w * (1 + reps / 30)) : 0);
// 'A'/'B' render as "Workout A/B"; program sessions store their label text
// ("Day A", "Push", ...) in workout_type and render it as-is.
const workoutLabel = (t) => (t === "A" || t === "B" ? `Workout ${t}` : t);
const SKIP_WARMUP_WITHIN = 20; // skip ramp sets this close to the working weight

// Effective bar weight for a lift: its override if set, else the global bar.
const barFor = (settings, lift) => {
  const o = settings.barOverrides && settings.barOverrides[lift];
  return o != null ? Number(o) : settings.bar;
};

/* ── v6 program mode ─────────────────────────────────────────────────────────
 * A student with users.program_id set trains their assigned program instead of
 * standard A/B. Both modes are normalized here into one "today" definition so
 * the Today screen, finish flow, and WIP persistence don't fork on mode:
 *   { mode, label, wipKey, lifts: [{ key, name, sets, reps, rest, equipment,
 *     tracksWeight, repUnit }], ... }
 * Program mode adds pos/count/programName. The rotation pointer is
 * settings.nextPos (user_settings.next_workout_position); it's position-based
 * and wraps to a valid index if the coach shrank the rotation since it was
 * written. settings.next ('A'/'B') is untouched in program mode so coming off
 * a program resumes standard A/B exactly where it was.
 */
function buildToday(state) {
  const program = state.program;
  if (program && Array.isArray(program.workouts) && program.workouts.length > 0) {
    const count = program.workouts.length;
    const raw = Number(state.settings.nextPos) || 0;
    const pos = ((raw % count) + count) % count; // wrap if the rotation shrank
    const w = program.workouts[pos];
    const seen = new Set();
    const lifts = [];
    for (const l of w.lifts) {
      if (seen.has(l.key)) continue; // defensive: log entries are keyed by lift
      seen.add(l.key);
      lifts.push({
        key: l.key, name: l.name || l.key,
        sets: Number(l.sets), reps: Number(l.reps),
        rest: Number(l.rest_seconds) || 180,
        equipment: l.equipment_type, tracksWeight: l.tracks_weight,
        repUnit: l.rep_unit || "reps",
      });
    }
    return {
      mode: "program", pos, count, programName: program.name,
      label: w.label, wipKey: `P:${program.id}:${pos}`, lifts,
    };
  }
  const next = state.settings.next;
  const keys = next === "A" ? WORKOUT_A : WORKOUT_B;
  return {
    mode: "ab",
    emptyProgram: !!program, // assigned but zero workouts: fall back with a notice
    label: `Workout ${next}`, wipKey: next,
    lifts: keys.map((k) => ({
      key: k, name: LIFTS[k].name, sets: LIFTS[k].sets, reps: 5,
      rest: state.settings.rest, equipment: "barbell",
      tracksWeight: "required", repUnit: "reps",
    })),
  };
}

// Unique lifts across every workout in a program, in rotation order, optionally
// filtered. Used by Setup (weights, bar overrides) and Plates (jump list).
function programLiftList(program, pred) {
  const seen = new Set();
  const out = [];
  if (!program || !Array.isArray(program.workouts)) return out;
  for (const w of program.workouts) {
    for (const l of w.lifts) {
      if (seen.has(l.key)) continue;
      seen.add(l.key);
      if (!pred || pred(l)) out.push(l);
    }
  }
  return out;
}

// Display name for any lift key: the DB lift library first, the built-in five
// as fallback, then the raw key (old history survives any library change).
const liftName = (liftMeta, k) =>
  (liftMeta && liftMeta[k] && liftMeta[k].name) || (LIFTS[k] && LIFTS[k].name) || k;

// A weight counts as "set" once it's a positive number. Unset weights show the
// inline set-your-weight input on the Today card (required/optional lifts).
const weightSet = (w) => Number(w) > 0;

// Target display: "3×8" or "3×60s" for timed holds.
const schemeText = (l) => `${l.sets}×${l.reps}${l.repUnit === "seconds" ? "s" : ""}`;

/* ── in-progress workout persistence ──────────────────────────────────────────
 * The active-but-unfinished workout entry (logged sets, notes, bodyweight) lives
 * in React memory. If the OS kills a backgrounded PWA (e.g. after a long rest
 * with a notification pending, under memory pressure), tapping the notification
 * cold-launches a fresh instance and that memory is gone. To avoid losing logged
 * sets, we mirror the entry to localStorage on every change and rehydrate it on
 * load — but only when it matches the current session (same workout A/B and same
 * calendar day), so a restore never applies stale data to the wrong workout.
 * Single-device by design; the server still gets the real data at finish time.
 */
const WIP_KEY = "llh_wip_workout";

/* ── local (Pacific) date ─────────────────────────────────────────────────────
 * The app is anchored to Pacific time (the school is in Sacramento; there is no
 * per-user timezone). Workout dates, WIP same-day matching, and the deload gap
 * are all Pacific so the client and server agree. Previously todayStr() used
 * toISOString().slice(0,10) = the UTC date, so a lift logged after ~5pm Pacific
 * was stamped under the NEXT day. Cosmetic before v7, but it would have broken
 * gap detection, same-day reminder suppression, and every-other-day parity.
 * Intl handles DST with no dependency. This is TRUE Pacific even on a device set
 * to another timezone, matching the server exactly (Setup tells the user so).
 */
const APP_TZ = "America/Los_Angeles";
const localDateStr = () => {
  const p = {};
  for (const part of new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date())) p[part.type] = part.value;
  return `${p.year}-${p.month}-${p.day}`;
};
const todayStr = () => localDateStr();

function loadWip(workoutType) {
  try {
    const raw = localStorage.getItem(WIP_KEY);
    if (!raw) return null;
    const wip = JSON.parse(raw);
    // Only restore if it's the same workout (A/B) and same day.
    if (wip && wip.workoutType === workoutType && wip.date === todayStr()) return wip;
  } catch (e) { /* ignore malformed/unavailable storage */ }
  return null;
}

function saveWip(workoutType, log, notes, bw) {
  try {
    localStorage.setItem(WIP_KEY, JSON.stringify({
      workoutType, date: todayStr(), log, notes, bw,
    }));
  } catch (e) { /* ignore */ }
}

function clearWip() {
  try { localStorage.removeItem(WIP_KEY); } catch (e) { /* ignore */ }
}

// True if any in-progress workout is stored for today (any A/B or program
// workout). Used to suppress the deload comeback prompt so it never lands on top
// of a mid-workout restore. Requires at least one actually-logged set, not just
// a same-day WIP record — the init/restore effect writes a blank same-day entry
// merely from opening the Today tab (before any set is tapped), which would
// otherwise self-suppress the prompt on every visit.
function wipIsForToday() {
  try {
    const raw = localStorage.getItem(WIP_KEY);
    if (!raw) return false;
    const wip = JSON.parse(raw);
    if (!wip || wip.date !== todayStr()) return false;
    const log = wip.log || {};
    return Object.values(log).some((arr) => Array.isArray(arr) && arr.some((v) => v !== null));
  } catch (e) { return false; }
}

function warmup(working, bar, lift) {
  // Deadlift and row don't get bar-only warmup sets; they start from loaded ramps.
  const noBar = lift === "deadlift" || lift === "row";
  if (working <= bar) return noBar ? [] : [{ w: bar, reps: 5, label: "Bar" }];
  const sets = noBar ? [] : [{ w: bar, reps: 5, label: "Bar" }, { w: bar, reps: 5, label: "Bar" }];
  const jumps = [0.45, 0.65, 0.85];
  const reps = [5, 3, 2];
  let last = bar;
  jumps.forEach((j, i) => {
    const w = r5(bar + (working - bar) * j);
    if (w > last && w < working && working - w >= SKIP_WARMUP_WITHIN) {
      sets.push({ w, reps: reps[i] }); last = w;
    }
  });
  return sets;
}

/* ── shared content header ───────────────────────────────────────────────── */
function AppTitle() {
  return (
    <div className="llh-app-title">
      IRONLIFTS <span className="llh-c-brass">5×5</span>
    </div>
  );
}

/* ── small UI atoms ──────────────────────────────────────────────────────── */

function SetChip({ target, value, onCycle }) {
  let cls = "llh-chip", label = target;
  if (value !== null) {
    label = value;
    if (value >= target) cls = "llh-chip llh-chip--done";
    else if (value === 0) cls = "llh-chip llh-danger";
    else cls = "llh-chip llh-danger llh-chip--partial";
  }
  return (
    <button onClick={onCycle} aria-label={`set ${value === null ? "not done" : value + " reps"}`} className={cls}>{label}</button>
  );
}

// Countdown lives at the shell level so it keeps running across in-app tab
// switches and the set chips can start it. It tracks an absolute end time and
// derives the remaining seconds, so it stays accurate and self-corrects when a
// throttled/backgrounded tab regains focus. `pulse` flips true for ~2s when the
// timer hits zero, driving the full-screen brass cue.
function useRestTimer() {
  const [left, setLeft] = useState(0);
  const [running, setRunning] = useState(false);
  const [pulse, setPulse] = useState(false);
  const endRef = useRef(0);       // absolute end time (ms epoch) while running
  const remainRef = useRef(0);    // remaining seconds while paused
  const intervalRef = useRef(null);
  const pulseOff = useRef(null);

  const stop = () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };

  const fireDone = useCallback(() => {
    stop();
    setRunning(false);
    setLeft(0);
    setPulse(true);
    clearTimeout(pulseOff.current);
    pulseOff.current = setTimeout(() => setPulse(false), 2000);
  }, []);

  const sample = useCallback(() => {
    const remaining = Math.max(0, Math.round((endRef.current - Date.now()) / 1000));
    setLeft(remaining);
    if (remaining <= 0) fireDone();
  }, [fireDone]);

  // Start (or restart) at a given number of seconds. Used by presets and chips.
  const start = useCallback((s) => {
    const secs = Number(s);
    if (!secs || secs <= 0) return;
    setPulse(false);
    clearTimeout(pulseOff.current);
    endRef.current = Date.now() + secs * 1000;
    setLeft(secs);
    setRunning(true);
    stop();
    intervalRef.current = setInterval(sample, 250);
  }, [sample]);

  const toggle = useCallback(() => {
    if (running) {
      const remaining = Math.max(0, Math.round((endRef.current - Date.now()) / 1000));
      remainRef.current = remaining;
      stop();
      setRunning(false);
      return { action: "paused", remaining };
    } else if (remainRef.current > 0) {
      const remaining = remainRef.current;
      endRef.current = Date.now() + remaining * 1000;
      stop();
      intervalRef.current = setInterval(sample, 250);
      setRunning(true);
      return { action: "resumed", remaining };
    }
    return { action: "none", remaining: 0 };
  }, [running, sample]);

  // Snap to the correct remaining time when a throttled tab comes back.
  useEffect(() => {
    const onWake = () => { if (intervalRef.current) sample(); };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [sample]);

  useEffect(() => () => { stop(); clearTimeout(pulseOff.current); }, []);

  return { left, running, pulse, start, toggle };
}

/* ── usePushNotifications ─────────────────────────────────────────────────────
 * Owns notification permission state and this device's push subscription, and
 * exposes schedule/cancel for the rest timer to call. Kept separate from
 * useRestTimer so the timing logic stays clean; the shell wires the two together.
 *
 * Flow:
 *  - permission starts as the browser's current Notification.permission.
 *  - The first time a timer starts while permission is "default", the shell shows
 *    an in-app prompt (explaining why) instead of firing the bare browser dialog.
 *    enable() runs the real requestPermission()+subscribe() only after the
 *    student opts in.
 *  - Once subscribed, scheduleAt(seconds, liftName) POSTs a server-side push to
 *    fire when rest ends; cancel() removes it (pause/finish).
 *  - If permission is denied/unavailable, scheduleAt/cancel are no-ops and the
 *    in-app brass pulse remains the only signal (handled by useRestTimer).
 */
function usePushNotifications() {
  const [permission, setPermission] = useState(
    PUSH_SUPPORTED ? Notification.permission : "unsupported"
  );
  const subIdRef = useRef(null);          // push_subscriptions.id from the server
  const subscribingRef = useRef(false);

  // Ensure we have a server-side subscription for this device. Safe to call
  // repeatedly; only does work once. Returns the subscription id or null.
  const ensureSubscribed = useCallback(async () => {
    if (!PUSH_SUPPORTED) return null;
    if (subIdRef.current) return subIdRef.current;
    if (subscribingRef.current) return null;
    subscribingRef.current = true;
    try {
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }
      const json = sub.toJSON();
      const { data } = await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      subIdRef.current = data.id;
      return data.id;
    } catch (e) {
      // Subscription failed (denied mid-flow, network, unsupported quirk). Leave
      // subId null; the timer still works in-app, just no background push.
      return null;
    } finally {
      subscribingRef.current = false;
    }
  }, []);

  // Called from the in-app prompt's "Enable" button. Fires the real browser
  // permission dialog, then subscribes if granted.
  const enable = useCallback(async () => {
    if (!PUSH_SUPPORTED) return "unsupported";
    let result = Notification.permission;
    if (result === "default") {
      result = await Notification.requestPermission();
    }
    setPermission(result);
    if (result === "granted") {
      await ensureSubscribed();
    }
    return result;
  }, [ensureSubscribed]);

  // Schedule a push to fire in `seconds`, naming the lift. No-op unless granted.
  const scheduleAt = useCallback(async (seconds, liftName) => {
    if (!PUSH_SUPPORTED || Notification.permission !== "granted") return;
    const id = await ensureSubscribed();
    if (!id) return;
    const body = liftName
      ? `That's enough rest, get back to ${liftName}!`
      : "That's enough rest, get back to it!";
    try {
      await api.post("/push/schedule", {
        subscription_id: id,
        seconds,
        title: "Rest's over",
        body,
      });
    } catch (e) {
      /* non-fatal: the in-app timer still fires the pulse */
    }
  }, [ensureSubscribed]);

  // Cancel this device's pending push (pause / finish / restart-clear).
  const cancel = useCallback(async () => {
    if (!PUSH_SUPPORTED || !subIdRef.current) return;
    try {
      await api.delete("/push/schedule", { data: { subscription_id: subIdRef.current } });
    } catch (e) {
      /* non-fatal */
    }
  }, []);

  return { permission, enable, scheduleAt, cancel };
}

function RestTimer({ timer }) {
  const { left, running, start, toggle, pushPermission } = timer;
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, "0");
  // Only show the fallback note when background alerts genuinely won't work —
  // permission denied, or the browser doesn't support push at all. Not shown by
  // default (i.e. while still "default"/"granted").
  const showFallbackNote =
    pushPermission === "denied" || pushPermission === "unsupported";
  return (
    <div>
      <div className="llh-row-wrap">
        <div className={running ? "llh-timer llh-c-brass" : "llh-timer llh-dim"}>{mm}:{ss}</div>
        {[90, 180, 300].map((s) => <button key={s} onClick={() => start(s)} className="llh-btn-ghost">{s < 120 ? `${s}s` : `${s / 60}m`}</button>)}
        {left > 0 && <button onClick={toggle} className="llh-btn-ghost">{running ? "Pause" : "Resume"}</button>}
      </div>
      {showFallbackNote && (
        <div className="llh-dim-11 llh-mt-8 llh-lh-14">
          Notifications are off — rest alerts only show while the app is open.
        </div>
      )}
    </div>
  );
}

/* ── inline weight entry ─────────────────────────────────────────────────────
 * Shown on a Today card when a weight-tracking lift has no stored weight yet
 * (new program lifts have no coach-set starting weight by design). Required
 * lifts must be set before the session can be finished; optional (bodyweight)
 * lifts can stay blank, which logs 0 added lb.
 */
function InlineWeight({ lift, onSave }) {
  const [val, setVal] = useState("");
  const required = lift.tracksWeight === "required";
  const ok = Number(val) > 0;
  return (
    <div className="llh-sep">
      <div className="llh-dim-12 llh-mb-6 llh-lh-14">
        {required
          ? "Set your working weight to log this lift."
          : "Bodyweight by default. Add a weight here if you're loading it."}
      </div>
      <div className="llh-inline-row">
        <input type="number" inputMode="decimal" value={val} onChange={(e) => setVal(e.target.value)} placeholder="lb"
          className="llh-weight-input" />
        <button onClick={() => ok && onSave(Number(val))} disabled={!ok} className="llh-btn-pill llh-btn--dim">
          Set weight
        </button>
      </div>
    </div>
  );
}

/* ── Today ───────────────────────────────────────────────────────────────── */
function Today({ state, today, persistFinish, saving, onPlateTap, timer, log, setLog, notes, setNotes, bw, setBw, saveWeight }) {
  const { settings } = state;
  const [open, setOpen] = useState({});
  const [summary, setSummary] = useState(null);

  const cycle = (l, i) => {
    setLog((prev) => {
      const arr = [...(prev[l.key] || Array(l.sets).fill(null))];
      const cur = arr[i];
      if (l.repUnit === "seconds") {
        // Timed holds are pass/fail: blank -> held (target) -> missed (0) -> blank.
        arr[i] = cur === null ? l.reps : cur === 0 ? null : 0;
      } else {
        arr[i] = cur === null ? l.reps : cur - 1 < 0 ? null : cur - 1;
      }
      return { ...prev, [l.key]: arr };
    });
    // Logging a set kicks off (or restarts) the rest clock. Program lifts carry
    // their coach-set rest; A/B lifts carry the global setting, as before.
    timer.start(l.rest, l.name);
  };

  const anyLogged = today.lifts.some((l) => (log[l.key] || []).some((v) => v !== null));
  // A weight-required lift with logged sets must have a weight before finishing,
  // or the session would silently record 0 lb for real work.
  const missingWeight = today.lifts.filter(
    (l) => l.tracksWeight === "required" && !weightSet(state.weights[l.key]) &&
      (log[l.key] || []).some((v) => v !== null)
  );
  const canFinish = anyLogged && missingWeight.length === 0 && !saving;

  const finish = async () => {
    const bwNum = Number(bw);
    const result = await persistFinish(today, log, notes, bwNum > 0 ? bwNum : null);
    if (result) setSummary(result);
  };

  if (summary) {
    return (
      <div>
        <AppTitle />
        <div className="llh-eyebrow">Logged</div>
        <h2 className="llh-h2">Session saved</h2>
        {summary.rows.map((r) => (
          <div key={r.key} className="llh-card llh-card--tight llh-flex-between">
            <span className="llh-fw-700">{r.name}</span>
            {summary.mode === "ab" ? (
              <span className={`llh-fs-13 ${r.deload ? "llh-c-crimson" : r.up ? "llh-c-done" : "llh-dim"}`}>
                {r.deload ? `deload → ${r.next} lb` : r.up ? `${r.from} → ${r.next} lb` : `hold ${r.next} lb`}
              </span>
            ) : (
              <span className={`llh-fs-13 llh-tnum ${r.hit >= r.total ? "llh-c-done" : "llh-dim"}`}>
                {r.hit}/{r.total} sets hit
              </span>
            )}
          </div>
        ))}
        <p className="llh-dim-13 llh-mt-10">
          Next session is {today.label}. Come back when you train.
        </p>
        <button onClick={() => setSummary(null)} className="llh-btn-primary llh-btn-primary--lg llh-w-full llh-mt-6">Start next workout →</button>
      </div>
    );
  }

  return (
    <div>
      <AppTitle />
      <div className="llh-today-head llh-mb-14">
        <div className="llh-eyebrow llh-flex-1">Today</div>
        <div className="llh-today-label">
          {today.label}
        </div>
        <div className="llh-today-sub">
          {today.mode === "program"
            ? today.programName
            : (settings.next === "A" ? "Squat · Bench · Row" : "Squat · Press · Deadlift")}
        </div>
      </div>
      {today.mode === "program" && today.count > 1 && (
        <div className="llh-rotation-note">
          Day {today.pos + 1} of {today.count} in the rotation
        </div>
      )}
      {today.emptyProgram && (
        <div className="llh-card llh-card--tight llh-dim-13 llh-lh-15">
          Your assigned program has no workouts yet, so you're on the standard
          A/B rotation for now. Check with your coach.
        </div>
      )}

      {today.lifts.map((l) => {
        const k = l.key;
        const w = Number(state.weights[k]) || 0;
        const hasW = weightSet(w);
        const isBar = l.equipment === "barbell";
        const wu = isBar && hasW ? warmup(w, barFor(settings, k), k) : [];
        const f = today.mode === "ab" ? state.fails[k] : 0;
        return (
          <div key={k} className="llh-card llh-card--tight">
            <div className="llh-flex-between">
              <div>
                <div className="llh-lift-name">{l.name}</div>
                <div className="llh-dim-12">
                  {schemeText(l)}{hasW ? ` · ${w} lb` : ""}
                  {today.mode === "ab" && f > 0 && <span className="llh-c-crimson"> · stall {f}/2</span>}
                </div>
              </div>
              {hasW && (
                <div className="llh-weight-big">
                  {w}<span className="llh-unit"> lb</span>
                </div>
              )}
            </div>

            {l.tracksWeight !== "none" && !hasW && <InlineWeight lift={l} onSave={(v) => saveWeight(k, v)} />}

            {isBar && hasW && (
              <>
                <button onClick={() => onPlateTap(w, k)} title="Open in plate calculator"
                  className="llh-plate-btn">
                  <Barbell weight={w} bar={barFor(settings, k)} plateMap={settings.plates} compact />
                  <div className="llh-dim-10 llh-mt-2">tap to load plates →</div>
                </button>

                <button onClick={() => setOpen((o) => ({ ...o, [k]: !o[k] }))} className="llh-btn-ghost llh-mt-8 llh-fs-12">
                  {open[k] ? "Hide warmup" : "Show warmup"}
                </button>
                {open[k] && (
                  <div className="llh-warmup-sep">
                    {wu.length === 0 && <div className="llh-dim-12">No warm-up sets for this lift.</div>}
                    {wu.map((s, i) => (
                      <button key={i} onClick={() => onPlateTap(s.w, k)} title="Open in plate calculator"
                        className="llh-warmup-row">
                        <span>{s.label || "Warmup"} {i + 1}</span>
                        <span><span className="llh-strong-text">{s.w} × {s.reps}</span><span className="llh-c-brass llh-ml-8">plates →</span></span>
                      </button>
                    ))}
                    <div className="llh-dim-11 llh-mt-4">Then work sets below at {w} lb.</div>
                  </div>
                )}
              </>
            )}

            <div className="llh-row-wrap llh-mt-12">
              {(log[k] || Array(l.sets).fill(null)).map((v, i) => <SetChip key={i} target={l.reps} value={v} onCycle={() => cycle(l, i)} />)}
            </div>
          </div>
        );
      })}

      <div className="llh-card llh-card--tight">
        <div className="llh-eyebrow llh-mb-6">Rest timer</div>
        <RestTimer timer={timer} />
      </div>

      <div className="llh-card llh-card--tight">
        <div className="llh-eyebrow llh-mb-8">Session notes</div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="How did it feel? Anything to remember (e.g. why a set was missed)."
          className="llh-input llh-textarea-input" />

        <div className="llh-bw-sep">
          <div className="llh-label">Bodyweight today (optional)</div>
          <div className="llh-row-gap10">
            <input type="number" inputMode="decimal" value={bw} onChange={(e) => setBw(e.target.value)} placeholder="lb"
              className="llh-input llh-w-120" />
            <span className="llh-dim-12">saved when you log the workout</span>
          </div>
        </div>
      </div>

      <button onClick={finish} disabled={!canFinish}
        className="llh-btn-primary llh-btn-primary--lg llh-w-full">
        {saving ? "Saving..." : "Finish & log workout"}
      </button>
      {missingWeight.length > 0 && (
        <div className="llh-c-crimson llh-fs-12 llh-mt-8 llh-lh-15">
          Set a working weight for {missingWeight.map((l) => l.name).join(", ")} before finishing.
        </div>
      )}
      <p className="llh-dim-11 llh-lh-15 llh-mt-10">
        {today.mode === "ab"
          ? "Tap a set to mark 5 reps. Tap again to count down for missed reps. All sets at 5 sends the weight up next time. Miss a lift three sessions running and it deloads 10%."
          : "Tap a set to mark it done at the target; tap again to count down for missed reps. Timed holds toggle between held and missed. Weights on this program don't change automatically; adjust them in Setup as your coach directs."}
      </p>
    </div>
  );
}

/* ── Plates ──────────────────────────────────────────────────────────────── */
function Plates({ state, initialTarget, initialLift }) {
  const { settings, weights, program, liftMeta } = state;
  // Jump targets: barbell lifts of the current mode with a weight set. Standard
  // A/B always has the original five; a program contributes its barbell lifts.
  const barKeys = program
    ? programLiftList(program, (l) => l.equipment_type === "barbell").map((l) => l.key)
    : ORDER;
  const jumpable = barKeys.filter((k) => Number(weights[k]) > 0);
  const firstKey = initialLift || jumpable[0] || barKeys[0] || "squat";
  const [target, setTarget] = useState(initialTarget || Number(weights[firstKey]) || settings.bar);
  const [targetLift, setTargetLift] = useState(firstKey);
  useEffect(() => { if (initialTarget) setTarget(initialTarget); }, [initialTarget]);
  useEffect(() => { if (initialLift) setTargetLift(initialLift); }, [initialLift]);
  const bar = barFor(settings, targetLift);
  const sol = solvePlates(target, bar, settings.plates);
  const counts = {};
  if (sol.ok) sol.plates.forEach((d) => (counts[d] = (counts[d] || 0) + 1));
  return (
    <div>
      <AppTitle />
      <h2 className="llh-h2 llh-text-center">Load the bar</h2>
      <div className="llh-card llh-card--tight">
        <div className="llh-row-gap10">
          <button onClick={() => setTarget((t) => Math.max(bar, t - 5))} className="llh-btn-round">−</button>
          <input type="number" value={target} onChange={(e) => setTarget(Number(e.target.value) || 0)} className="llh-plate-input" />
          <button onClick={() => setTarget((t) => t + 5)} className="llh-btn-round">+</button>
        </div>
        <div className="llh-my-14"><Barbell weight={target} bar={bar} plateMap={settings.plates} /></div>
        {sol.ok ? (
          <>
            <div className="llh-dim-13 llh-text-center">
              {sol.perSide} lb per side{!sol.exact && <span className="llh-c-brass"> · closest is {sol.achieved} lb</span>}
            </div>
            <div className="llh-row-center-wrap llh-mt-12">
              {Object.keys(counts).sort((a, b) => b - a).map((d) => (
                <div key={d} className="llh-plate-chip">
                  <span className="llh-plate-swatch" style={{ background: (PLATE[d] || {}).c }} />
                  <span className="llh-fw-800">{d}</span><span className="llh-dim-13">× {counts[d]}/side</span>
                </div>
              ))}
            </div>
          </>
        ) : <div className="llh-dim llh-text-center">Target is below the {bar} lb bar.</div>}
      </div>
      {jumpable.length > 0 && (
        <>
          <div className="llh-eyebrow llh-mt-18 llh-mb-6">Jump to a lift</div>
          <div className="llh-row-wrap">
            {jumpable.map((k) => <button key={k} onClick={() => { setTarget(Number(weights[k])); setTargetLift(k); }} className={Number(weights[k]) === Number(target) && k === targetLift ? "llh-btn-pill" : "llh-btn-ghost"}>{liftName(liftMeta, k)} {weights[k]}</button>)}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Progress ────────────────────────────────────────────────────────────── */
function Progress({ state }) {
  const { program, liftMeta } = state;
  // Chartable lifts: the current mode's lifts plus anything with a stored
  // weight (covers pre-program A/B history after a switch). Lifts that track
  // no weight (e.g. plank) have nothing to chart.
  const chartable = [];
  {
    const base = program ? programLiftList(program, () => true).map((l) => l.key) : ORDER;
    const seen = new Set();
    for (const k of [...base, ...Object.keys(state.weights)]) {
      if (seen.has(k)) continue;
      seen.add(k);
      const meta = liftMeta && liftMeta[k];
      if (meta && meta.tracks_weight === "none") continue;
      chartable.push(k);
    }
  }
  const [sel, setSel] = useState(chartable[0] || "squat");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bw, setBw] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/progress/${sel}`).then(({ data }) => {
      if (!active) return;
      setData(data.map((r) => ({
        date: String(r.workout_date).slice(5, 10),
        weight: Number(r.weight_lb),
        reps: r.top_reps,
      })));
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [sel]);

  useEffect(() => {
    let active = true;
    api.get("/bodyweight").then(({ data }) => {
      if (active) setBw(data.map((r) => ({ date: String(r.logged_date).slice(5, 10), weight: Number(r.weight_lb) })));
    }).catch(() => active && setBw([]));
    return () => { active = false; };
  }, []);

  const cur = state.weights[sel];
  const best = data.reduce((m, d) => Math.max(m, d.weight), 0);
  return (
    <div>
      <AppTitle />
      <h2 className="llh-h2 llh-text-center">Progress</h2>
      <div className="llh-row-center-wrap llh-mb-12">
        {chartable.map((k) => <button key={k} onClick={() => setSel(k)} className={k === sel ? "llh-btn-pill" : "llh-btn-ghost"}>{liftName(liftMeta, k)}</button>)}
      </div>
      <div className="llh-row-gap10 llh-mb-12">
        <Stat label="Current" value={cur || "—"} />
        <Stat label="Est. 1RM" value={cur ? e1rm(cur, 5) : "—"} />
        <Stat label="Best logged" value={best || "—"} accent />
      </div>
      <div className={`llh-card llh-chart-card llh-h-250 ${data.length ? "llh-pi-stretch" : "llh-pi-center"}`}>
        {loading ? <div className="llh-spin" /> :
          data.length === 0 ? <div className="llh-dim-13">No sessions logged yet. Finish a workout to start the chart.</div> :
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 14, bottom: 0, left: -10 }}>
              <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "var(--dim)", fontSize: 10 }} interval="preserveStartEnd" minTickGap={26} stroke="var(--line)" />
              <YAxis tick={{ fill: "var(--dim)", fontSize: 10 }} stroke="var(--line)" width={36} domain={["dataMin - 10", "dataMax + 15"]} />
              <Tooltip contentStyle={{ background: "var(--raised)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--text)" }}
                labelStyle={{ color: "var(--dim)" }} formatter={(v, n, p) => [`${v} lb × ${p.payload.reps}`, "Top set"]} />
              <Line type="monotone" dataKey="weight" stroke="var(--brass)" strokeWidth={2.5} dot={{ r: 2, fill: "var(--brass)" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>}
      </div>

      <div className="llh-eyebrow llh-text-center llh-mt-18 llh-mb-8">Bodyweight</div>
      <div className={`llh-card llh-chart-card llh-h-220 ${(bw && bw.length >= 2) ? "llh-pi-stretch" : "llh-pi-center"}`}>
        {bw === null ? <div className="llh-spin" /> :
          bw.length < 2 ? <div className="llh-dim-13 llh-text-center llh-px-16 llh-lh-15">Log your bodyweight on at least two days to see the trend. You can add it on the Today screen when you finish a workout, or from your Account.</div> :
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={bw} margin={{ top: 4, right: 14, bottom: 0, left: -12 }}>
              <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "var(--dim)", fontSize: 10 }} interval="preserveStartEnd" minTickGap={26} stroke="var(--line)" />
              <YAxis tick={{ fill: "var(--dim)", fontSize: 10 }} stroke="var(--line)" width={38} domain={["dataMin - 3", "dataMax + 3"]} />
              <Tooltip contentStyle={{ background: "var(--raised)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--text)" }} labelStyle={{ color: "var(--dim)" }} formatter={(v) => [`${v} lb`, "Bodyweight"]} />
              <Line type="monotone" dataKey="weight" stroke="var(--brass-bright)" strokeWidth={2.5} dot={{ r: 2, fill: "var(--brass-bright)" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>}
      </div>
    </div>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div className="llh-card llh-stat-card">
      <div className={`llh-stat-value ${accent ? "llh-c-brass" : "llh-strong-text"}`}>{value}</div>
      <div className="llh-tag">{label}</div>
    </div>
  );
}

/* ── History ─────────────────────────────────────────────────────────────── */
function History({ liftMeta }) {
  const [sessions, setSessions] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/sessions").then(({ data }) => setSessions(data)).catch((e) => setErr(errMsg(e)));
  }, []);

  return (
    <div>
      <AppTitle />
      <h2 className="llh-h2 llh-text-center">History</h2>
      {err && <div className="llh-error-text">{err}</div>}
      {sessions === null ? <div className="llh-load-center"><div className="llh-spin" /></div> :
        sessions.length === 0 ? <div className="llh-dim llh-text-center llh-p-20">No sessions logged yet.</div> :
        sessions.map((s) => {
          const byLift = {};
          (s.sets || []).forEach((st) => {
            if (!byLift[st.exercise]) byLift[st.exercise] = { reps: [], weight: Number(st.weight_lb) };
            byLift[st.exercise].reps.push(st.reps);
          });
          return (
            <div key={s.id} className="llh-card llh-card--tight">
              <div className="llh-session-head">
                <span className="llh-session-title">{workoutLabel(s.workout_type)}</span>
                <span className="llh-dim-12">{String(s.workout_date).slice(0, 10)}</span>
              </div>
              {Object.keys(byLift).map((k) => {
                const l = byLift[k];
                const allHit = l.reps.every((r) => r >= 5);
                return (
                  <div key={k} className="llh-lift-row">
                    <span>{liftName(liftMeta, k)}</span>
                    <span className={`llh-tnum ${allHit ? "llh-c-done" : "llh-strong-text"}`}>
                      {l.weight} lb · {l.reps.join("/")}
                    </span>
                  </div>
                );
              })}
              {s.notes && <div className="llh-session-notes">{s.notes}</div>}
              {(s.comments || []).map((c) => (
                <div key={c.id} className="llh-comment">
                  <div className="llh-comment-author llh-mb-3">Coach {c.coach}</div>
                  <div className="llh-comment-body">{c.body}</div>
                </div>
              ))}
            </div>
          );
        })}
    </div>
  );
}

/* ── Setup ───────────────────────────────────────────────────────────────── */
function Setup({ state, today, saveSettings, saveWeight, reminders, applyDeloadNow, deloadBusy }) {
  const s = state.settings;
  const { program, liftMeta } = state;
  const isProgram = today.mode === "program";
  const BAR_OPTIONS = [55, 45, 35, 25, 15];

  // Turn reminders on/off for this account. On requires notification permission
  // and a push subscription on THIS device first (enable() handles both, even if
  // permission was already granted for the rest timer). Off just clears the
  // account flag; the subscription stays, shared with the rest timer. If the
  // schedule was still 'off', enabling seeds every-other-day so "on" does
  // something immediately and never leaves an invalid on+off state.
  const toggleReminders = async () => {
    if (s.remindersEnabled) { saveSettings({ remindersEnabled: false }); return; }
    const result = await reminders.enable();
    if (result === "granted") {
      const patch = { remindersEnabled: true };
      if (!s.reminderScheduleType || s.reminderScheduleType === "off") {
        patch.reminderScheduleType = "every_other_day";
      }
      saveSettings(patch);
    }
    // denied / unsupported: leave the flag off; the fine print explains why.
  };

  // Switch to a custom weekday schedule. The server rejects custom with an empty
  // day list, so if none are chosen yet seed today's weekday to keep it valid.
  const chooseCustom = () => {
    const cur = Array.isArray(s.reminderDays) ? s.reminderDays : [];
    if (cur.length === 0) {
      const wd = new Date(todayStr() + "T00:00:00Z").getUTCDay();
      saveSettings({ reminderScheduleType: "custom", reminderDays: [wd] });
    } else {
      saveSettings({ reminderScheduleType: "custom" });
    }
  };

  // Toggle one weekday. Never allow the last day to be removed (server rejects an
  // empty custom list); to stop reminders entirely, switch off the toggle.
  const toggleReminderDay = (i) => {
    const cur = Array.isArray(s.reminderDays) ? s.reminderDays : [];
    let next;
    if (cur.includes(i)) {
      next = cur.filter((d) => d !== i);
      if (next.length === 0) return;
    } else {
      next = [...cur, i].sort((a, b) => a - b);
    }
    saveSettings({ reminderDays: next });
  };

  // Which lifts each card manages. Standard A/B: the original five. Program:
  // the assigned program's lifts (barbell ones for bar settings, weight-tracking
  // ones for working weights).
  const barLiftKeys = isProgram
    ? programLiftList(program, (l) => l.equipment_type === "barbell").map((l) => l.key)
    : ORDER;
  const weightLifts = isProgram
    ? programLiftList(program, (l) => l.tracks_weight !== "none")
    : ORDER.map((k) => ({ key: k, tracks_weight: "required" }));

  // Show the per-lift section if the user already has overrides, or if they
  // explicitly toggle it on this session.
  const [perLift, setPerLift] = useState(
    !!(s.barOverrides && Object.keys(s.barOverrides).length > 0)
  );

  // Toggle between "same bar for all" and "different bars per lift".
  const setVariable = (on) => {
    setPerLift(on);
    // Switching back to a single bar clears all overrides so nothing lingers.
    if (!on && s.barOverrides && Object.keys(s.barOverrides).length > 0) {
      saveSettings({ barOverrides: {} });
    }
  };

  // Set (or clear) one lift's override. Setting it equal to the global bar just
  // removes the override, keeping the map minimal.
  const setLiftBar = (lift, bar) => {
    const next = { ...(s.barOverrides || {}) };
    if (bar === s.bar) delete next[lift];
    else next[lift] = bar;
    saveSettings({ barOverrides: next });
  };

  return (
    <div>
      <AppTitle />
      <h2 className="llh-h2 llh-text-center">Your setup</h2>

      {isProgram && (
        <div className="llh-card llh-card--tight">
          <div className="llh-row-label">Your program</div>
          <div className="llh-name">{today.programName}</div>
          <div className="llh-dim-12 llh-mt-4 llh-lh-15">
            A {today.count}-workout rotation assigned by your coach. Set targets and
            rest times come from the program; working weights are yours to manage below.
          </div>
        </div>
      )}

      {(!isProgram || barLiftKeys.length > 0) && (
        <div className="llh-card llh-card--tight">
          <div className="llh-row-label">Bar weight</div>
          <div className="llh-row-wrap">
            {BAR_OPTIONS.map((b) => <button key={b} onClick={() => saveSettings({ bar: b })} className={s.bar === b ? "llh-btn-pill" : "llh-btn-ghost"}>{b} lb</button>)}
          </div>

          <div className="llh-bw-sep">
            <div className="llh-row-wrap">
              <button onClick={() => setVariable(false)} className={!perLift ? "llh-btn-pill" : "llh-btn-ghost"}>Same bar for all lifts</button>
              <button onClick={() => setVariable(true)} className={perLift ? "llh-btn-pill" : "llh-btn-ghost"}>Different bars per lift</button>
            </div>

            {perLift && (
              <div className="llh-mt-12">
                <div className="llh-dim-11 llh-mb-10 llh-lh-15">
                  Set a bar for any lift that differs from your default of {s.bar} lb. Lifts left at the default follow it.
                </div>
                {barLiftKeys.map((k) => {
                  const eff = barFor(s, k);
                  return (
                    <div key={k} className="llh-setup-row">
                      <span className="llh-fs-14">{liftName(liftMeta, k)}</span>
                      <div className="llh-btn-row">
                        {BAR_OPTIONS.map((b) => (
                          <button key={b} onClick={() => setLiftBar(k, b)} className={eff === b ? "llh-btn-pill" : "llh-btn-ghost"}>{b}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {(!isProgram || barLiftKeys.length > 0) && (
        <div className="llh-card llh-card--tight">
          <div className="llh-row-label">Plates you own</div>
          <div className="llh-row-wrap">
            {[55, 45, 35, 25, 10, 5, 2.5, 1.25].map((d) => (
              <button key={d} onClick={() => saveSettings({ plates: { ...s.plates, [d]: !s.plates[d] } })} className={s.plates[d] ? "llh-btn-pill" : "llh-btn-ghost"}>{d}</button>
            ))}
          </div>
        </div>
      )}

      {!isProgram && (
        <div className="llh-card llh-card--tight">
          <div className="llh-row-label">Increment per workout (lb)</div>
          {ORDER.map((k) => (
            <div key={k} className="llh-inc-row">
              <span className="llh-fs-14">{LIFTS[k].name}</span>
              <div className="llh-btn-row">
                {[2.5, 5, 10].map((inc) => <button key={inc} onClick={() => saveSettings({ increments: { ...s.increments, [k]: inc } })} className={s.increments[k] === inc ? "llh-btn-pill" : "llh-btn-ghost"}>+{inc}</button>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isProgram && (
        <div className="llh-card llh-card--tight">
          <div className="llh-row-label">Rest between sets</div>
          <div className="llh-row-wrap">
            {[[90, "90s"], [180, "3m"], [300, "5m"]].map(([sec, lbl]) => (
              <button key={sec} onClick={() => saveSettings({ rest: sec })} className={s.rest === sec ? "llh-btn-pill" : "llh-btn-ghost"}>{lbl}</button>
            ))}
          </div>
          <div className="llh-dim-11 llh-mt-8">Used when you tap a set to start the rest clock. You can still pick a different length on the fly from the Today screen.</div>
        </div>
      )}

      <div className="llh-card llh-card--tight">
        <div className="llh-row-label">Current working weights</div>
        {weightLifts.map((l) => (
          <div key={l.key} className="llh-weight-row">
            <span className="llh-fs-14">
              {liftName(liftMeta, l.key)}
              {l.tracks_weight === "optional" && <span className="llh-dim-11"> (added lb, optional)</span>}
            </span>
            <input type="number" defaultValue={state.weights[l.key] ?? ""} onBlur={(e) => saveWeight(l.key, Number(e.target.value) || 0)}
              className="llh-setup-weight-input" />
          </div>
        ))}
        <div className="llh-dim-11 llh-mt-6">
          {isProgram
            ? "Edit and tap away to save. Weights on this program only change when you change them here or on the Today screen."
            : "Edit and tap away to save. Set these to where you're actually lifting now."}
        </div>
      </div>

      <div className="llh-card llh-card--tight">
        <div className="llh-row-label">Next workout</div>
        {isProgram ? (
          <div className="llh-row-wrap">
            {program.workouts.map((w, i) => (
              <button key={i} onClick={() => saveSettings({ nextPos: i })} className={today.pos === i ? "llh-btn-pill" : "llh-btn-ghost"}>{w.label}</button>
            ))}
          </div>
        ) : (
          <div className="llh-inline-row">
            {["A", "B"].map((w) => <button key={w} onClick={() => saveSettings({ next: w })} className={s.next === w ? "llh-btn-pill" : "llh-btn-ghost"}>Workout {w}</button>)}
          </div>
        )}
      </div>

      <div className="llh-card llh-card--tight">
        <div className="llh-row-label">Workout reminders</div>
        {reminders.permission === "unsupported" ? (
          <div className="llh-dim-11 llh-lh-15">
            This browser can't show notifications, so reminders aren't available here.
          </div>
        ) : (
          <>
            <div className="llh-row-wrap">
              <button onClick={toggleReminders} className={s.remindersEnabled ? "llh-btn-pill" : "llh-btn-ghost"}>
                {s.remindersEnabled ? "Reminders on" : "Reminders off"}
              </button>
            </div>

            {s.remindersEnabled && reminders.permission !== "granted" && (
              <div className="llh-dim-11 llh-mt-8 llh-lh-15 llh-c-crimson">
                Reminders are on for your account, but notifications aren't enabled on
                this device, so it won't alert here. Tap the toggle to enable them on
                this device.
              </div>
            )}

            {s.remindersEnabled && (
              <div className="llh-mt-12">
                <div className="llh-row-label">Which days</div>
                <div className="llh-row-wrap">
                  <button onClick={() => saveSettings({ reminderScheduleType: "every_other_day" })}
                    className={s.reminderScheduleType === "every_other_day" ? "llh-btn-pill" : "llh-btn-ghost"}>Every other day</button>
                  <button onClick={chooseCustom}
                    className={s.reminderScheduleType === "custom" ? "llh-btn-pill" : "llh-btn-ghost"}>Specific days</button>
                </div>

                {s.reminderScheduleType === "every_other_day" && (
                  <div className="llh-dim-11 llh-mt-8 llh-lh-15">
                    Reminds you two days after each workout. Lifting off-schedule resets
                    the count from your last session.
                  </div>
                )}

                {s.reminderScheduleType === "custom" && (
                  <div className="llh-mt-8">
                    <div className="llh-row-wrap">
                      {WEEKDAYS.map((d, i) => {
                        const on = Array.isArray(s.reminderDays) && s.reminderDays.includes(i);
                        return (
                          <button key={i} onClick={() => toggleReminderDay(i)}
                            className={on ? "llh-btn-pill" : "llh-btn-ghost"}>{d}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="llh-mt-12">
                  <div className="llh-row-label">Reminder time</div>
                  <input type="time" value={s.reminderTime || "08:00"}
                    onChange={(e) => e.target.value && saveSettings({ reminderTime: e.target.value })}
                    className="llh-setup-weight-input" />
                  <div className="llh-dim-11 llh-mt-6 llh-lh-15">
                    Times are Pacific. Even if your phone is set to another timezone,
                    reminders use Sacramento time.
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="llh-card llh-card--tight">
        <div className="llh-row-label">Comeback deload</div>
        <div className="llh-inline-row">
          <input type="number" inputMode="numeric" min="1" max="50" defaultValue={s.deloadPct}
            onBlur={(e) => {
              let v = Math.round(Number(e.target.value));
              if (!Number.isFinite(v)) v = s.deloadPct;
              v = Math.min(50, Math.max(1, v));
              e.target.value = v;
              if (v !== s.deloadPct) saveSettings({ deloadPct: v });
            }}
            className="llh-setup-weight-input" />
          <span className="llh-fs-14">percent</span>
        </div>
        <div className="llh-dim-11 llh-mt-6 llh-lh-15">
          Used both when you miss a week and come back, and when a lift stalls three
          sessions in a row. Rounded to the nearest 5 lb, never below the empty bar.
        </div>
        {isProgram ? (
          <div className="llh-dim-11 llh-mt-10 llh-lh-15">
            Program weights are coach-set, so there's no automatic deload to trigger here.
          </div>
        ) : (
          <div className="llh-mt-10">
            <button onClick={applyDeloadNow} disabled={deloadBusy} className="llh-install-btn-ghost">
              {deloadBusy ? "Applying..." : `Deload now (${s.deloadPct}%)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── shell ───────────────────────────────────────────────────────────────── */
export default function Ironlifts() {
  // Tab is the URL: Today is /app, any other tab is /app?tab=<id>. Deriving it
  // from search params (instead of local state) keeps history depth at most two
  // ([/app] then [/app?tab=X]) so the hardware back button returns to Today
  // rather than unmounting the shell and killing the rest timer.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "today";
  // True once we've pushed a ?tab= entry above /app this visit, so tapping Today
  // knows whether history.back() has an /app to land on.
  const pushedTabRef = useRef(false);
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [plateTarget, setPlateTarget] = useState(null);
  const [plateTargetLift, setPlateTargetLift] = useState(null);
  const [unread, setUnread] = useState(0);
  // In-progress workout entry lives here (not in Today) so navigating to Setup,
  // Plates, etc. and back doesn't wipe logged sets or notes.
  const [log, setLog] = useState({});
  const [notes, setNotes] = useState("");
  const [bw, setBw] = useState("");
  // Settings save indicator (v6): settings writes are no longer silently
  // optimistic — the pill reflects saving/saved/error so a dropped write is
  // visible instead of reverting mysteriously later.
  const [saveState, setSaveState] = useState("idle");
  const saveDoneRef = useRef(null);
  // Missed-week comeback prompt (v7).
  const [deloadStatus, setDeloadStatus] = useState(null);
  const [showDeload, setShowDeload] = useState(false);
  const [deloadResult, setDeloadResult] = useState(null); // summary after an A/B accept
  const [deloadBusy, setDeloadBusy] = useState(false);

  useEffect(() => {
    api.get("/feedback/unread").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
  }, []);

  // Tab changes drive the URL under a bounded history model: push at most one
  // ?tab= entry above /app, replace when moving between non-Today tabs, and pop
  // (history.back) to leave a pushed tab so Today is reached without a dead
  // back-press. React Router keeps the element mounted across search-param
  // changes, so the rest timer and WIP state survive tab switches.
  const selectTab = (id) => {
    if (id === tab) return;
    if (id !== "today" && tab === "today") {
      setSearchParams({ tab: id });
      pushedTabRef.current = true;
    } else if (id !== "today") {
      setSearchParams({ tab: id }, { replace: true });
    } else if (pushedTabRef.current) {
      // We pushed an entry above /app; pop it so the URL (and tab) return to
      // Today via popstate, leaving no orphaned forward entry.
      window.history.back();
    } else {
      // Landed directly on ?tab=X (deep link or refresh): nothing beneath to pop
      // to, so rewrite the URL in place.
      setSearchParams({}, { replace: true });
    }
  };

  // Reaching a tab (by tap or back-navigation) reconciles side effects: reset
  // the pushed flag on Today, and clear the unread coach-feedback badge on
  // History. Living in an effect (not the click handler) means popstate-driven
  // arrivals get the same treatment. The unread guard prevents a re-run loop.
  useEffect(() => {
    if (tab === "today") pushedTabRef.current = false;
    if (tab === "history" && unread > 0) {
      setUnread(0);
      api.post("/feedback/mark-read").catch(() => {});
    }
  }, [tab, unread]);
  const timer = useRestTimer();
  const push = usePushNotifications();
  // The in-app "enable notifications?" prompt. Shown the first time a timer
  // starts while permission is still "default" and the student hasn't dismissed
  // it before. pendingResume holds nothing here — the prompt is non-blocking;
  // the timer starts regardless, the prompt only governs background push.
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  // Remember the most recent rest length + lift so "Enable" can schedule the
  // already-running timer, and so resume can re-schedule.
  const lastRestRef = useRef({ seconds: 0, liftName: null });

  const pushDismissed = () => {
    try { return localStorage.getItem(PUSH_DISMISSED_KEY) === "1"; }
    catch (e) { return false; }
  };

  // Start (or restart) a rest period and coordinate background push.
  const startRest = useCallback((seconds, liftName) => {
    timer.start(seconds);
    lastRestRef.current = { seconds, liftName: liftName || null };
    if (!PUSH_SUPPORTED) return;
    if (push.permission === "granted") {
      push.scheduleAt(seconds, liftName);
    } else if (push.permission === "default" && !pushDismissed()) {
      // First-run, in-context: ask via our own card before the browser dialog.
      setShowPushPrompt(true);
    }
    // denied/unsupported: do nothing; in-app pulse is the only signal.
  }, [timer, push]);

  // Pause/resume, coordinating the server-side push so a paused timer can't fire.
  const toggleRest = useCallback(() => {
    const r = timer.toggle();
    if (!PUSH_SUPPORTED || push.permission !== "granted") return;
    if (r.action === "paused") {
      push.cancel();
    } else if (r.action === "resumed" && r.remaining > 0) {
      push.scheduleAt(r.remaining, lastRestRef.current.liftName);
    }
  }, [timer, push]);

  // The prompt's "Enable" handler: turn on permission, and if granted schedule
  // the rest period that's already counting down.
  const enablePush = useCallback(async () => {
    setShowPushPrompt(false);
    const result = await push.enable();
    if (result === "granted" && timer.running && lastRestRef.current.seconds > 0) {
      push.scheduleAt(timer.left || lastRestRef.current.seconds, lastRestRef.current.liftName);
    }
  }, [push, timer]);

  const dismissPush = useCallback(() => {
    setShowPushPrompt(false);
    try { localStorage.setItem(PUSH_DISMISSED_KEY, "1"); } catch (e) { /* ignore */ }
  }, []);

  // Bundle passed down in place of the raw timer so Today/RestTimer drive the
  // coordinated versions (and can read permission for the fallback note).
  const restControl = {
    left: timer.left,
    running: timer.running,
    pulse: timer.pulse,
    start: startRest,
    toggle: toggleRest,
    pushPermission: push.permission,
  };

  // Reminders reuse the same permission + subscribe flow as the rest timer.
  // enable() requests permission and subscribes this device even if permission
  // was already granted, so Setup only needs these two.
  const remindersControl = {
    permission: push.permission,
    enable: push.enable,
  };

  const goToPlates = (weight, lift) => { setPlateTarget(weight); if (lift) setPlateTargetLift(lift); selectTab("plates"); };

  const load = useCallback(async () => {
    try {
      const [wRes, sRes, pRes, lRes] = await Promise.all([
        api.get("/weights"), api.get("/settings"), api.get("/program"), api.get("/lifts"),
      ]);
      const weights = { ...DEFAULT_WEIGHTS };
      const fails = { squat: 0, bench: 0, row: 0, ohp: 0, deadlift: 0 };
      let needSeed = wRes.data.length === 0;
      wRes.data.forEach((r) => { weights[r.exercise] = Number(r.current_weight_lb); fails[r.exercise] = r.fail_count; });
      if (needSeed) {
        await Promise.all(ORDER.map((k) => api.put("/weights", { exercise: k, current_weight_lb: DEFAULT_WEIGHTS[k], fail_count: 0 })));
      }
      const row = sRes.data || {};
      const settings = {
        bar: Number(row.bar_weight) || DEFAULT_SETTINGS.bar,
        plates: row.available_plates || DEFAULT_SETTINGS.plates,
        increments: { ...DEFAULT_SETTINGS.increments, ...(row.increments || {}) },
        next: row.next_workout || DEFAULT_SETTINGS.next,
        rest: Number(row.rest_duration) || DEFAULT_SETTINGS.rest,
        barOverrides: row.bar_overrides || {},
        // v6 rotation pointer; 0 is a real value, so no || fallback.
        nextPos: row.next_workout_position != null ? Number(row.next_workout_position) : 0,
        // v7 reminders + deload
        reminderScheduleType: row.reminder_schedule_type || "off",
        reminderDays: Array.isArray(row.reminder_days) ? row.reminder_days : [],
        reminderTime: row.reminder_time ? String(row.reminder_time).slice(0, 5) : "08:00",
        remindersEnabled: !!row.reminders_enabled,
        deloadPct: row.deload_pct != null ? Number(row.deload_pct) : 10,
      };
      const liftMeta = {};
      (lRes.data || []).forEach((l) => { liftMeta[l.key] = l; });
      setState({ weights, fails, settings, program: pRes.data || null, liftMeta });
      // Missed-week comeback check. Non-fatal: if it fails the prompt just won't
      // show. Suppressed while a workout is in progress today so it never lands
      // on top of a mid-workout restore.
      try {
        const dl = await api.get("/deload/status");
        setDeloadStatus(dl.data);
        if (dl.data && dl.data.prompt_due && !wipIsForToday()) setShowDeload(true);
      } catch (e) { /* ignore */ }
    } catch (e) {
      setErr(errMsg(e, "Could not load your data"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // One normalized definition of what's being trained today (A/B or program).
  const today = state ? buildToday(state) : null;
  const wipKey = today ? today.wipKey : null;

  // Reset log/notes/bw when the workout identity changes (first load, A/B
  // flip, or program rotation advance/assignment change), following React's
  // documented "adjust state when a prop changes" pattern: the reset runs
  // during render, not in an effect, so it lands in the SAME commit as the
  // wipKey change. There's no intermediate render where an effect could see
  // stale (pre-switch) or not-yet-settled (post-restore) values — the mirror
  // effect below only ever observes a fully-settled state, so it can't
  // mistake "restored/reset" for "genuinely edited" and write a phantom
  // same-day entry before any set is actually logged.
  const [wipStateKey, setWipStateKey] = useState(wipKey);
  const wipBaselineRef = useRef(null);
  if (wipKey !== wipStateKey) {
    setWipStateKey(wipKey);
    const restored = wipKey ? loadWip(wipKey) : null;
    const fresh = {};
    if (wipKey) {
      today.lifts.forEach((l) => {
        const arr = restored && restored.log && restored.log[l.key];
        fresh[l.key] = Array.isArray(arr) && arr.length === l.sets
          ? arr
          : Array(l.sets).fill(null);
      });
    }
    const freshNotes = restored && typeof restored.notes === "string" ? restored.notes : "";
    const freshBw = restored && restored.bw != null ? String(restored.bw) : "";
    setLog(fresh);
    setNotes(freshNotes);
    setBw(freshBw);
    wipBaselineRef.current = { log: fresh, notes: freshNotes, bw: freshBw };
  }

  // Mirror the in-progress entry to localStorage whenever it's genuinely
  // edited, so a backgrounded-then-killed app can restore it. Skips the write
  // when log/notes/bw still reference-match the restore/reset baseline above.
  useEffect(() => {
    if (!wipKey) return;
    const base = wipBaselineRef.current;
    if (base && base.log === log && base.notes === notes && base.bw === bw) return;
    saveWip(wipKey, log, notes, bw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wipKey, log, notes, bw]);

  /* Settings writes (v6 persistence fix): plain axios PUTs could be dropped if
   * the app was closed right after a change — the request never survived the
   * page teardown, so the setting silently reverted on the next load or on
   * another device. fetch with keepalive lets the browser finish the request
   * after the page is gone. Local state still updates immediately; the pill
   * shows saving/saved/error so failures are visible. */
  const saveSettings = async (patch) => {
    const next = { ...state.settings, ...patch };
    setState((s) => ({ ...s, settings: next }));
    setSaveState("saving");
    clearTimeout(saveDoneRef.current);
    try {
      const res = await fetch("/api/settings", {
        method: "PUT",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokens.get()}`,
        },
        body: JSON.stringify({
          bar_weight: next.bar, available_plates: next.plates,
          increments: next.increments, next_workout: next.next,
          rest_duration: next.rest, bar_overrides: next.barOverrides,
          next_workout_position: next.nextPos,
          reminder_schedule_type: next.reminderScheduleType,
          reminder_days: next.reminderDays,
          reminder_time: next.reminderTime,
          reminders_enabled: next.remindersEnabled,
          deload_pct: next.deloadPct,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSaveState("saved");
      saveDoneRef.current = setTimeout(() => setSaveState("idle"), 1600);
    } catch (e) {
      setSaveState("error");
      saveDoneRef.current = setTimeout(() => setSaveState("idle"), 4000);
    }
  };

  const saveWeight = async (k, val) => {
    setState((s) => ({ ...s, weights: { ...s.weights, [k]: val } }));
    setSaveState("saving");
    clearTimeout(saveDoneRef.current);
    try {
      await keepaliveJson("PUT", "/weights", { exercise: k, current_weight_lb: val, fail_count: state.fails[k] || 0 });
      setSaveState("saved");
      saveDoneRef.current = setTimeout(() => setSaveState("idle"), 1600);
    } catch (e) {
      setSaveState("error");
      saveDoneRef.current = setTimeout(() => setSaveState("idle"), 4000);
    }
  };

  /* Missed-week comeback deload. A/B students: drop the five lifts by the
   * configured percent, rounded to 5 and floored at each lift's effective bar,
   * fail counts reset to 0, one PUT /weights each (the same write path finish
   * uses). Program students: no writes by design (decision 8), just acknowledge.
   * Both outcomes POST /deload/ack so the prompt won't re-fire this gap. */
  const acceptDeload = async () => {
    if (deloadBusy) return;
    setDeloadBusy(true);
    const pct = (deloadStatus && Number(deloadStatus.deload_pct)) || state.settings.deloadPct || 10;
    try {
      if (deloadStatus && deloadStatus.has_program) {
        await api.post("/deload/ack", { action: "accepted" });
        setShowDeload(false);
      } else {
        const rows = [];
        const newWeights = { ...state.weights }, newFails = { ...state.fails };
        for (const k of ORDER) {
          const from = Number(state.weights[k]) || 0;
          const floor = barFor(state.settings, k);
          const to = Math.max(r5(from * (1 - pct / 100)), floor);
          newWeights[k] = to;
          newFails[k] = 0;
          await api.put("/weights", { exercise: k, current_weight_lb: to, fail_count: 0 });
          rows.push({ key: k, name: liftName(state.liftMeta, k), from, to });
        }
        await api.post("/deload/ack", { action: "accepted" });
        setState((s) => ({ ...s, weights: newWeights, fails: newFails }));
        setDeloadResult({ pct, rows });
      }
    } catch (e) {
      setErr(errMsg(e, "Could not apply the deload"));
      setShowDeload(false);
    } finally {
      setDeloadBusy(false);
    }
  };

  /* Manual deload trigger (Setup page). Same A/B weight-drop as acceptDeload's
   * missed-week path, but user-initiated instead of gated on a 7+ day gap.
   * Also acks the missed-week prompt so it doesn't immediately re-fire on top
   * of a deload the user just applied by hand. Program students don't have a
   * fixed lift set to drop, so this is A/B-only (Setup hides the button
   * otherwise). */
  const applyDeloadNow = async () => {
    if (deloadBusy || state.program) return;
    const pct = state.settings.deloadPct || 10;
    if (!window.confirm(`Deload your working weights by ${pct}%? This lowers all five lifts and resets stall counts.`)) return;
    setDeloadBusy(true);
    try {
      const rows = [];
      const newWeights = { ...state.weights }, newFails = { ...state.fails };
      for (const k of ORDER) {
        const from = Number(state.weights[k]) || 0;
        const floor = barFor(state.settings, k);
        const to = Math.max(r5(from * (1 - pct / 100)), floor);
        newWeights[k] = to;
        newFails[k] = 0;
        await api.put("/weights", { exercise: k, current_weight_lb: to, fail_count: 0 });
        rows.push({ key: k, name: liftName(state.liftMeta, k), from, to });
      }
      await api.post("/deload/ack", { action: "accepted" }).catch(() => {});
      setState((s) => ({ ...s, weights: newWeights, fails: newFails }));
      setDeloadResult({ pct, rows });
      setShowDeload(true);
    } catch (e) {
      setErr(errMsg(e, "Could not apply the deload"));
    } finally {
      setDeloadBusy(false);
    }
  };

  const declineDeload = async () => {
    if (deloadBusy) return;
    setShowDeload(false);
    try { await api.post("/deload/ack", { action: "declined" }); }
    catch (e) { /* non-fatal: server keeps the prompt armed for next open */ }
  };

  const dismissDeloadResult = () => { setShowDeload(false); setDeloadResult(null); };

  const persistFinish = async (todayDef, log, notes, bodyweight) => {
    setSaving(true); setErr("");
    try {
      const lifts = todayDef.lifts.map((l) => ({
        exercise: l.key,
        weight: Number(state.weights[l.key]) || 0,
        reps: (log[l.key] || Array(l.sets).fill(null)).map((v) => (v === null ? 0 : v)),
      }));
      const date = todayStr(); // Pacific date, not UTC (see localDateStr)
      // Standard sessions store 'A'/'B'; program sessions store the label text
      // ("Day A"), which History renders as-is and which survives program
      // renames or deletion.
      const workoutType = todayDef.mode === "ab" ? state.settings.next : todayDef.label;
      await api.post("/sessions", { workout_date: date, workout_type: workoutType, notes: notes || null, lifts });

      // Optional bodyweight entry from the Today screen. Best-effort: the session
      // is already saved, so a failure here must not abort the finish flow.
      if (bodyweight) {
        try { await api.post("/bodyweight", { weight_lb: bodyweight }); }
        catch (e) { /* non-fatal; user can re-log on the Account page */ }
      }

      if (todayDef.mode === "ab") {
        // Standard A/B keeps the validated 5x5 auto-progression exactly as-is.
        const rows = [];
        const newWeights = { ...state.weights }, newFails = { ...state.fails };
        for (const l of todayDef.lifts) {
          const k = l.key;
          const reps = log[k].map((v) => (v === null ? 0 : v));
          const success = reps.length === LIFTS[k].sets && reps.every((r) => r >= 5);
          const inc = state.settings.increments[k];
          const from = state.weights[k];
          let next = from, deload = false, up = false;
          if (success) { next = from + inc; up = true; newFails[k] = 0; }
          else {
            const f = state.fails[k] + 1;
            if (f >= 3) {
              // v7: configured deload percent (default 10), floored at the bar.
              // The stall deload historically had no floor; adding one is a
              // deliberate small improvement so a light lift can't drop below the
              // empty bar.
              const pct = state.settings.deloadPct || 10;
              next = Math.max(r5(from * (1 - pct / 100)), barFor(state.settings, k));
              deload = true; newFails[k] = 0;
            }
            else newFails[k] = f;
          }
          newWeights[k] = next;
          await api.put("/weights", { exercise: k, current_weight_lb: next, fail_count: newFails[k] });
          rows.push({ key: k, name: l.name, from, next, up, deload });
        }
        const nextWorkout = state.settings.next === "A" ? "B" : "A";
        await api.put("/settings", { next_workout: nextWorkout });
        // Workout is saved to the server — clear the in-progress local copy so the
        // next session (the switched A/B) starts clean and can't restore stale data.
        clearWip();
        setState((s) => ({ ...s, weights: newWeights, fails: newFails, settings: { ...s.settings, next: nextWorkout } }));
        return { mode: "ab", rows };
      }

      // Program mode: log only. No auto-progression by design (weights are
      // manual until v6.5 progression rules); advance the rotation pointer.
      const rows = todayDef.lifts.map((l) => {
        const reps = (log[l.key] || []).map((v) => (v === null ? 0 : v));
        return { key: l.key, name: l.name, hit: reps.filter((r) => r >= l.reps).length, total: l.sets };
      });
      const nextPos = (todayDef.pos + 1) % todayDef.count;
      await api.put("/settings", { next_workout_position: nextPos });
      clearWip();
      setState((s) => ({ ...s, settings: { ...s.settings, nextPos } }));
      return { mode: "program", rows };
    } catch (e) {
      setErr(errMsg(e, "Could not save the workout"));
      return null;
    } finally { setSaving(false); }
  };

  if (!state) {
    return <div className="llh-full-center">{err ? <div className="llh-c-crimson">{err}</div> : <div className="llh-spin" />}</div>;
  }

  // A program with no barbell lifts has no use for the plate calculator.
  const hasBarbell = !state.program ||
    programLiftList(state.program, (l) => l.equipment_type === "barbell").length > 0;
  const tabs = [
    ["today", "Today"],
    ...(hasBarbell ? [["plates", "Plates"]] : []),
    ["progress", "Progress"], ["history", "History"], ["setup", "Setup"],
  ];

  return (
    <div className="llh-page llh-pb-48">
      {timer.pulse && (
        <div aria-hidden="true" className="llh-restpulse-overlay" />
      )}
      {showPushPrompt && (
        <div className="llh-install-floating">
          <div className="llh-install-card">
            <div className="llh-install-eyebrow">
              Rest timer alerts
            </div>
            <div className="llh-body-text">
              Get notified when your rest is over, even if you lock your phone or
              switch apps. We'll ask your browser for permission next.
            </div>
            <div className="llh-actions-end">
              <button onClick={dismissPush} className="llh-install-btn-ghost">
                Not now
              </button>
              <button onClick={enablePush} className="llh-install-btn-brass">
                Enable alerts
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeload && deloadStatus && (
        <div className="llh-install-floating">
          <div className="llh-install-card">
            {deloadResult ? (
              <>
                <div className="llh-install-eyebrow">Deloaded {deloadResult.pct}%</div>
                <div className="llh-body-text">
                  Your working weights are set for an easier comeback:
                </div>
                <div className="llh-mt-8">
                  {deloadResult.rows.map((r) => (
                    <div key={r.key} className="llh-inc-row">
                      <span className="llh-fs-14">{r.name}</span>
                      <span className="llh-fs-13 llh-dim">{r.from} → {r.to} lb</span>
                    </div>
                  ))}
                </div>
                <div className="llh-actions-end">
                  <button onClick={dismissDeloadResult} className="llh-install-btn-brass">Got it</button>
                </div>
              </>
            ) : (
              <>
                <div className="llh-install-eyebrow">Welcome back</div>
                <div className="llh-body-text">
                  {deloadStatus.has_program
                    ? `It's been ${deloadStatus.gap_days} days since your last workout. Consider reducing your working weights by about ${deloadStatus.deload_pct} percent when you come back, then build back up.`
                    : `It's been ${deloadStatus.gap_days} days since your last workout. Deload your lifts by ${deloadStatus.deload_pct} percent to come back safely?`}
                </div>
                <div className="llh-actions-end">
                  <button onClick={declineDeload} disabled={deloadBusy} className="llh-install-btn-ghost">Not now</button>
                  <button onClick={acceptDeload} disabled={deloadBusy} className="llh-install-btn-brass">
                    {deloadStatus.has_program ? "Got it" : (deloadBusy ? "Applying..." : `Deload ${deloadStatus.deload_pct}%`)}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {!showPushPrompt && !showDeload && <InstallBanner variant="floating" />}
      {saveState !== "idle" && (
        <div aria-live="polite" className={saveState === "error" ? "llh-save-pill llh-danger" : "llh-save-pill"}>
          {saveState === "saving" ? "Saving..." : saveState === "saved" ? "Saved" : "Couldn't save. Check your connection and try again."}
        </div>
      )}
      <header className="llh-header-sticky llh-header-sticky--z30">
        <div className="llh-tabbar">
          <div className="llh-tabs">
            {tabs.map(([id, label]) => (
              <button key={id} onClick={() => selectTab(id)} className={tab === id ? "llh-tab llh-tab--active" : "llh-tab"}>
                {label}
                {id === "history" && unread > 0 && (
                  <span className="llh-unread-badge">{unread}</span>
                )}
              </button>
            ))}
          </div>
          <AccountMenu />
        </div>
      </header>

      {err && <div className="llh-err-wrap">
        <div className="llh-err-box">{err}</div>
      </div>}

      <main className="llh-app-main">
        {tab === "today" && <Today state={state} today={today} persistFinish={persistFinish} saving={saving} onPlateTap={goToPlates} timer={restControl} log={log} setLog={setLog} notes={notes} setNotes={setNotes} bw={bw} setBw={setBw} saveWeight={saveWeight} />}
        {tab === "plates" && hasBarbell && <Plates state={state} initialTarget={plateTarget} initialLift={plateTargetLift} />}
        {tab === "progress" && <Progress state={state} />}
        {tab === "history" && <History liftMeta={state.liftMeta} />}
        {tab === "setup" && <Setup state={state} today={today} saveSettings={saveSettings} saveWeight={saveWeight} reminders={remindersControl} applyDeloadNow={applyDeloadNow} deloadBusy={deloadBusy} />}
      </main>
    </div>
  );
}
